import { isAnyOf, UnknownAction } from '@reduxjs/toolkit'
import { createReducer } from '@reduxjs/toolkit/react'
import { AssistantItem, AssistantItems, Item, Traceable } from '@repo/shared/dtos/assistant'
import { generateId } from '@repo/shared/uuid/uuid'
import { isEqual } from 'lodash'

import { completeInsightDisplayDatasetsColors } from '@/components/admin/insights/chat/dataset'
import { adminStoreActions, storeActions } from '@/store/actions'
import { Task } from '@/store/task'

export type AssistantFeedItem =
  | AssistantItem
  | Tasks
  | Tips
  | (Item<'cta'> & {
      cta: 'generateDescription'
    })
  | (Item<'cta'> & {
      cta: 'applyDescription'
    })
  | (Item<'cta'> & {
      cta: 'generateMethodology'
    })
  | (Item<'cta'> & {
      cta: 'applyMethodology'
    })
  | (Item<'cta'> & {
      cta: 'askForMethodology'
    })
  | (Item<'cta'> & {
      cta: 'askForResources'
    })
  | (Item<'cta'> & {
      cta: 'askForMembers'
    })
  | (Item<'cta'> & {
      cta: 'askForComplete'
    })

type Tasks = {
  id: string
  generationId: string
  tasks: Task[]
  type: 'tasks'
}
type Tips = {
  id: string
  generationId: string
  tips: AssistantItems['tip'][]
  type: 'tips'
}

export type AssistantState = {
  feed: AssistantFeedItem[]
  view: {
    dashboard: {
      beneficiaryProfiles: {
        value: AssistantItems['beneficiaryProfile'][]
        isLoading: boolean
      }
    }
  }
  generationId?: string
  isThinking: boolean
  loadingTransKeys: string[]
  pendingActions: string[]
  isGeneratingBeneficiaryProfile: boolean
  isGeneratingBeneficiaryStructuredData: boolean
  isGeneratingTask: boolean
  isGeneratingMessage: boolean
  tempData?: { content: string } & Traceable
}

export const assistantInitialState: AssistantState = {
  view: {
    dashboard: {
      beneficiaryProfiles: {
        value: [],
        isLoading: false,
      },
    },
  },
  feed: [],
  isThinking: false,
  isGeneratingBeneficiaryProfile: false,
  isGeneratingBeneficiaryStructuredData: false,
  isGeneratingTask: false,
  isGeneratingMessage: false,
  loadingTransKeys: [],
  pendingActions: [],
  tempData: undefined,
}

export const assistantReducer: ((
  state: AssistantState | undefined,
  action: UnknownAction,
) => AssistantState) & {
  getInitialState: () => AssistantState
} = createReducer(assistantInitialState, (builder) => {
  // Organization
  builder
    .addCase(storeActions.cleanUpOrganization, () => {
      return assistantInitialState
    })
    .addCase(
      storeActions.initOrganization.pending,
      (
        _,
        {
          meta: {
            arg: { isSameOrganization },
          },
        },
      ) => {
        if (isSameOrganization) return
        return assistantInitialState
      },
    )

  builder
    // View: Dashboard
    .addCase(storeActions.initDashboard, () => {
      return assistantInitialState
    })
    .addCase(
      storeActions.assistant.generate.dashboard.beneficiaryProfiles.start,
      (state, { payload: { generationId } }) => {
        if (isInvalidGenerationId(state, generationId)) return
        state.view.dashboard.beneficiaryProfiles.isLoading = true
        state.feed = []
        state.generationId = generationId
        state.loadingTransKeys = generateCaseworkerDashboardTransKeys
      },
    )
    .addCase(storeActions.assistant.generate.dashboard.beneficiaryProfiles.end, (state) => {
      state.view.dashboard.beneficiaryProfiles.isLoading = false
    })
    .addCase(
      storeActions.assistant.add.dashboard.beneficiaryProfile,
      (state, { payload: { item } }) => {
        if ('cta' in item && item.cta === 'profile')
          state.view.dashboard.beneficiaryProfiles.value.push(item)
      },
    )

    // View: Beneficiary
    .addCase(
      storeActions.beneficiary.initBeneficiary.pending,
      (
        _,
        {
          meta: {
            arg: { isSameBeneficiary },
          },
        },
      ) => {
        if (isSameBeneficiary) return
        return assistantInitialState
      },
    )
    .addCase(storeActions.view.beneficiary.profile, (state, { payload: { generationId } }) => {
      const hasTasksInFeed = state.feed.some((item) => item.type === 'tasks')
      if (state.isGeneratingTask || state.isGeneratingMessage || hasTasksInFeed) return state
      return {
        ...assistantInitialState,
        generationId,
      }
    })
    .addCase(storeActions.view.beneficiary.tasks, (state, { payload: { generationId } }) => {
      const hasTasksInFeed = state.feed.some((item) => item.type === 'tasks')
      if (state.isGeneratingTask || state.isGeneratingMessage || hasTasksInFeed) return state
      return {
        ...assistantInitialState,
        generationId,
      }
    })
    .addCase(storeActions.beneficiary.refreshProfile.pending, (state) => {
      state.isGeneratingBeneficiaryProfile = true
    })
    .addCase(storeActions.beneficiary.refreshProfile.rejected, (state) => {
      state.isGeneratingBeneficiaryProfile = false
    })
    .addCase(storeActions.beneficiary.refreshProfile.fulfilled, (state) => {
      state.isGeneratingBeneficiaryProfile = false
    })
    .addCase(storeActions.beneficiary.refreshStructuredData.pending, (state) => {
      state.isGeneratingBeneficiaryStructuredData = true
    })
    .addCase(storeActions.beneficiary.refreshStructuredData.rejected, (state) => {
      state.isGeneratingBeneficiaryStructuredData = false
    })
    .addCase(storeActions.beneficiary.refreshStructuredData.fulfilled, (state) => {
      state.isGeneratingBeneficiaryStructuredData = false
    })

    // Generating Tasks
    .addCase(
      storeActions.assistant.generate.beneficiary.caseWorkerTasks.start,
      (state, { payload: { generationId } }) => {
        if (isInvalidGenerationId(state, generationId)) return
        state.generationId = generationId
        state.isGeneratingTask = true
      },
    )
    .addCase(storeActions.assistant.generate.beneficiary.caseWorkerTasks.end, (state) => {
      state.isGeneratingTask = false
    })

    // Onboarding
    .addCase(adminStoreActions.onboarding.init.start, (_, { payload: { generationId } }) => {
      return {
        ...assistantInitialState,
        generationId,
      }
    })

    // Admin: Description
    .addCase(
      storeActions.assistant.askFor.organization.description.start,
      (_, { payload: { generationId } }) => {
        return {
          ...assistantInitialState,
          generationId,
        }
      },
    )
    // Admin: Methodology
    .addCase(
      storeActions.assistant.askFor.organization.methodology.start,
      (_, { payload: { generationId } }) => {
        return {
          ...assistantInitialState,
          generationId,
        }
      },
    )

  builder.addCase(
    storeActions.assistant.replace.feed.item,
    (state, { payload: { itemIndex, newItem } }) => {
      safeFillFeed(state, newItem, itemIndex)
    },
  )

  builder.addCase(
    storeActions.assistant.remove.feed.item,
    // TODO: update to handle item.id or item.generationId or item.type or itemIndex
    (state, { payload: { itemIndex } }) => {
      state.feed.splice(itemIndex, 1)
    },
  )

  builder.addCase(storeActions.assistant.add.feed.item, (state, { payload: { item } }) => {
    if (isInvalidGenerationId(state, item.generationId)) return

    switch (item.type) {
      // Group tips together
      case 'tip':
        {
          const tips = state.feed.find((item) => item.type === 'tips')
          if (tips) {
            // Do not add the same tip twice
            const isFound = tips.tips.some(
              (v) => v.id === item.id || v.title === item.title || v.content === item.content,
            )
            if (!isFound) tips.tips.push(item)
          } else {
            safeFillFeed(state, {
              id: generateId(),
              generationId: item.generationId,
              tips: [item],
              type: 'tips',
            })
          }
        }
        break
      // Group tasks together by type
      case 'task':
        {
          const tasks = state.feed.find((item) => item.type === 'tasks')
          if (tasks && tasks.type === 'tasks') {
            // Do not add the same task twice
            const isFound = tasks.tasks.some(
              (v) => v.id === item.id || v.title === item.title || v.content === item.content,
            )
            if (!isFound) tasks.tasks.push(item)
          } else {
            safeFillFeed(state, {
              id: generateId(),
              generationId: item.generationId,
              tasks: [item],
              type: 'tasks',
            })
          }
        }
        break

      default:
        safeFillFeed(state, item)
        break
    }
  })

  builder.addCase(
    adminStoreActions.organization.generateDescription.fulfilled,
    (state, { payload: { description, traceUrl } }) => {
      state.tempData = { content: description, traceUrl }
    },
  )

  builder.addCase(
    adminStoreActions.organization.generateMethodology.fulfilled,
    (state, { payload: { methodology, traceUrl } }) => {
      state.tempData = { content: methodology, traceUrl }
    },
  )

  builder
    .addCase(adminStoreActions.initAnalyticsChat, (_, { payload: { generationId } }) => {
      return {
        ...assistantInitialState,
        generationId,
      }
    })

    .addCase(
      storeActions.assistant.askFor.beneficiary.information.fulfilled,
      (
        state,
        {
          meta: {
            arg: { generationId },
          },
          payload,
        },
      ) => {
        const { output, traceUrl } = payload
        if (isInvalidGenerationId(state, generationId)) return

        state.isGeneratingMessage = false
        safeFillFeed(state, {
          content: output,
          generationId,
          id: generateId(),
          sentAt: new Date().getTime(),
          traceUrl,
          type: 'message',
        })
      },
    )

    .addCase(
      storeActions.assistant.askFor.beneficiaries.information.fulfilled,
      (
        state,
        {
          meta: {
            arg: { generationId },
          },
          payload: { items },
        },
      ) => {
        if (isInvalidGenerationId(state, generationId)) return

        state.isGeneratingMessage = false

        for (const item of items) {
          if (item.type === 'insightDisplay') {
            // Complete the chart colors by picking random colors for the datasets
            // in a next version, the LLM will be able to set the colors
            completeInsightDisplayDatasetsColors(item)
          }
          safeFillFeed(state, item)
        }
      },
    )
    .addCase(
      storeActions.assistant.generate.beneficiary.moreTasks,
      (state, { payload: { generationId } }) => {
        if (isInvalidGenerationId(state, generationId)) return
        state.generationId = generationId
        state.loadingTransKeys = generateMoreTasksTransKeys
      },
    )

  builder.addMatcher(
    isAnyOf(
      adminStoreActions.organization.generateDescription.pending,
      adminStoreActions.organization.generateMethodology.pending,
    ),
    (
      state,
      {
        meta: {
          arg: { generationId },
        },
      },
    ) => {
      if (isInvalidGenerationId(state, generationId)) return
      state.loadingTransKeys = generateOrganizationDescriptionTransKeys
    },
  )

  builder
    .addMatcher(
      isAnyOf(
        storeActions.assistant.askFor.beneficiary.information.pending,
        storeActions.assistant.askFor.beneficiaries.information.pending,
      ),
      (
        state,
        {
          meta: {
            arg: { generationId, input },
          },
        },
      ) => {
        if (isInvalidGenerationId(state, generationId)) return
        state.isGeneratingMessage = true

        safeFillFeed(state, {
          content: input,
          generationId,
          id: generateId(),
          isFromUser: true,
          sentAt: new Date().getTime(),
          type: 'message',
        })
      },
    )
    .addMatcher(
      isAnyOf(
        storeActions.assistant.askFor.beneficiary.information.rejected,
        storeActions.assistant.askFor.beneficiaries.information.rejected,
      ),
      (state) => {
        state.isGeneratingMessage = false
      },
    )

  builder
    .addMatcher(
      isAnyOf(
        // Onboarding
        adminStoreActions.onboarding.init.start,
        adminStoreActions.organization.generateDescription.pending,
        adminStoreActions.organization.generateMethodology.pending,
        adminStoreActions.onboarding.setNextStep.pending,

        // Listening to the socket
        storeActions.assistant.generate.dashboard.beneficiaryProfiles.start,
        storeActions.assistant.generate.beneficiary.caseWorkerTasks.start,
        storeActions.assistant.askFor.organization.description.start,
        storeActions.assistant.askFor.organization.updateDescription.start,
        storeActions.assistant.askFor.organization.methodology.start,
        storeActions.assistant.askFor.organization.updateMethodology.start,
        storeActions.assistant.askFor.beneficiary.information.pending,
        storeActions.assistant.askFor.beneficiaries.information.pending,
      ),
      (state, meta) => {
        safeUpdateIsThinking({
          metaType: meta.type,
          isThinking: true,
          state,
        })
      },
    )
    .addMatcher(
      isAnyOf(
        // Onboarding
        adminStoreActions.onboarding.init.end,
        adminStoreActions.organization.generateDescription.fulfilled,
        adminStoreActions.organization.generateDescription.rejected,
        adminStoreActions.organization.generateMethodology.fulfilled,
        adminStoreActions.organization.generateMethodology.rejected,
        adminStoreActions.onboarding.setNextStep.fulfilled,
        adminStoreActions.onboarding.setNextStep.rejected,

        // Listening to the socket
        storeActions.assistant.generate.dashboard.beneficiaryProfiles.end,
        storeActions.assistant.generate.beneficiary.caseWorkerTasks.end,
        storeActions.assistant.askFor.organization.description.end,
        storeActions.assistant.askFor.organization.updateDescription.end,
        storeActions.assistant.askFor.organization.methodology.end,
        storeActions.assistant.askFor.organization.updateMethodology.end,
        storeActions.assistant.askFor.beneficiary.information.rejected,
        storeActions.assistant.askFor.beneficiary.information.fulfilled,
        storeActions.assistant.askFor.beneficiaries.information.rejected,
        storeActions.assistant.askFor.beneficiaries.information.fulfilled,
      ),
      (state, meta) => {
        safeUpdateIsThinking({
          metaType: meta.type,
          isThinking: false,
          state,
        })
      },
    )

  // // Detect changes in generationId
  // builder.addMatcher(isAllOf(), (state, action) => {
  //   const newGenerationId = (action as Action)?.payload?.generationId
  //   if (newGenerationId)
  //     console.warn('AJ: Set newGenerationId:', {
  //       type: action.type,
  //       isEqual: state.generationId === newGenerationId,
  //       state: state.generationId,
  //       new: newGenerationId,
  //     })
  // })
})

function isInvalidGenerationId(state: AssistantState, generationId: string): boolean {
  return !!state.generationId && state.generationId !== generationId
}

const safeFillFeed = (
  state: AssistantState,
  item: AssistantFeedItem,
  replaceItemIndex?: number,
): AssistantFeedItem[] => {
  if (isInvalidGenerationId(state, item.generationId)) return []
  if (replaceItemIndex !== undefined) {
    // Replace the item at the specified index
    state.feed[replaceItemIndex] = item
  } else {
    // Do not add the same item twice
    const existingItemIndex = state.feed.findIndex(
      (v) =>
        // avoid same id
        v.id === item.id ||
        // avoid exact same item
        isEqual(v, item) ||
        // avoid same cta
        (v.type === 'cta' &&
          'cta' in v &&
          'cta' in item &&
          v.cta === item.cta &&
          'content' in item &&
          'content' in v &&
          v.content === item.content) ||
        // avoid same scripted message
        ('contentTransKey' in v &&
          'contentTransKey' in item &&
          v.contentTransKey === item.contentTransKey),
    )
    if (existingItemIndex === -1) state.feed.push(item)
  }
  return state.feed
}

const safeUpdateIsThinking = ({
  metaType,
  isThinking,
  state,
}: PendingActionsParams & {
  isThinking: boolean
}) => {
  if (!metaType) throw Error('metaType is required')

  if (isThinking && state.loadingTransKeys.length === 0) state.loadingTransKeys = ['thinking']

  if (isThinking == true) {
    addToPendingActions({ metaType, state })

    state.isThinking = true
  } else {
    removeFromPendingActions({ metaType, state })

    if (state.pendingActions.length >= 1) return

    // Only set isThinking to false if there are no pending actions
    state.isThinking = false
    state.loadingTransKeys = []
  }
}

type PendingActionsParams = { metaType: string; state: AssistantState }
function addToPendingActions({ metaType, state }: PendingActionsParams) {
  const cleanType = cleanMetaType(metaType)
  const found = state.pendingActions.findIndex((type) => type === cleanType)
  if (found >= 0) return
  state.pendingActions.push(cleanType)
}

function removeFromPendingActions({ metaType, state }: PendingActionsParams) {
  const pendingActionIndex = state.pendingActions.findIndex(
    (type) => type === cleanMetaType(metaType),
  )
  if (pendingActionIndex >= 0) state.pendingActions.splice(pendingActionIndex, 1)
}

const cleanMetaType = (metaType: string): string => {
  return metaType.replace(/\/(pending|fulfilled|rejected)$/, '')
}

export const generateTipsTransKeys = ['analyzingProfile', 'generatingTips']

const generateCaseworkerDashboardTransKeys = ['analyzingProfiles', 'generatingTips']

const generateOrganizationDescriptionTransKeys = ['resourcesBrowsing', 'writingSummary']

const generateMoreTasksTransKeys = [
  'browsingExistingTasks',
  'readingNotes',
  'browsingResources',
  'writingTasks',
]
